import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  WarningAmber as WarningIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import JSZip from "jszip";
import { useAuth } from "../hooks/useAuth";
import AppService from "../services/AppService";
import { ListingService } from "../services/ListingService";
import IconService from "../services/IconService";
import FeatureGraphicService from "../services/FeatureGraphicService";
import ScreenshotService from "../services/ScreenshotService";
import { MarketingService } from "../services/MarketingService";
import AppViolationService from "../services/AppViolationService";
import ChecklistService from "../services/ChecklistService";
import type { App } from "../types/app";
import type { AppStoreListing } from "../types/listing";
import type { GeneratedIcon } from "../types/icon";
import type { GeneratedFeatureGraphic } from "../types/featureGraphic";
import type { LocalizedScreenshot } from "../types/screenshot";
import type {
  MarketingAsset,
  MarketingResearchReport,
} from "../types/marketing";
import type { AppViolationReviewReport } from "../types/appViolation";
import type { ChecklistProgress } from "../types/checklist";

function downloadText(
  content: string,
  fileName: string,
  mimeType = "application/json",
) {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function copyText(value: string) {
  if (!value.trim()) return;
  await navigator.clipboard.writeText(value);
}

function openDownload(url: string, fileName?: string) {
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  if (fileName) a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

const PublishReadyDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState<App[]>([]);
  const [selectedAppId, setSelectedAppId] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloadingPack, setDownloadingPack] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [listings, setListings] = useState<AppStoreListing[]>([]);
  const [icons, setIcons] = useState<GeneratedIcon[]>([]);
  const [featureGraphics, setFeatureGraphics] = useState<
    GeneratedFeatureGraphic[]
  >([]);
  const [screenshots, setScreenshots] = useState<LocalizedScreenshot[]>([]);
  const [marketingReports, setMarketingReports] = useState<
    MarketingResearchReport[]
  >([]);
  const [marketingAssets, setMarketingAssets] = useState<MarketingAsset[]>([]);
  const [violationReports, setViolationReports] = useState<
    AppViolationReviewReport[]
  >([]);
  const [checklistProgress, setChecklistProgress] =
    useState<ChecklistProgress | null>(null);

  const selectedApp = useMemo(
    () => apps.find((app) => app.appId === selectedAppId) || null,
    [apps, selectedAppId],
  );

  const latestMarketingReport = marketingReports[0] || null;
  const latestViolationReport = violationReports[0] || null;

  const loadApps = useCallback(async () => {
    if (!user?.email) return;
    const result = await AppService.getUserApps(user.email);
    setApps(result);
  }, [user?.email]);

  const loadPublishData = useCallback(async () => {
    if (!user?.email || !selectedAppId) return;
    setLoading(true);
    setError(null);
    try {
      const [
        appListings,
        appIcons,
        appFeatureGraphics,
        appScreenshots,
        appMarketingReports,
        appMarketingAssets,
        appViolationReports,
        appChecklist,
      ] = await Promise.all([
        ListingService.getAppListings(selectedAppId, user.email),
        IconService.getIconsByAppId(selectedAppId, user.email),
        FeatureGraphicService.getFeatureGraphicsByAppId(
          selectedAppId,
          user.email,
        ),
        ScreenshotService.getScreenshotsByAppId(selectedAppId, user.email),
        MarketingService.getAppMarketingReports(user.email, selectedAppId),
        MarketingService.getAppMarketingAssets(user.email, selectedAppId),
        AppViolationService.getReportsByApp(user.email, selectedAppId),
        ChecklistService.getChecklistProgress(selectedAppId, user.email),
      ]);

      setListings(appListings);
      setIcons(appIcons);
      setFeatureGraphics(appFeatureGraphics);
      setScreenshots(appScreenshots);
      setMarketingReports(appMarketingReports);
      setMarketingAssets(appMarketingAssets);
      setViolationReports(appViolationReports);
      setChecklistProgress(appChecklist);
    } catch (loadError: any) {
      console.error(loadError);
      setError(loadError?.message || "Failed to load publish-ready data.");
    } finally {
      setLoading(false);
    }
  }, [selectedAppId, user?.email]);

  useEffect(() => {
    loadApps().catch((loadError) => {
      console.error(loadError);
      setError("Failed to load apps.");
    });
  }, [loadApps]);

  useEffect(() => {
    loadPublishData();
  }, [loadPublishData]);

  const readySections = [
    listings.length > 0,
    icons.length > 0,
    featureGraphics.length > 0,
    screenshots.length > 0,
    marketingReports.length > 0,
    violationReports.length > 0,
    Boolean(checklistProgress),
  ].filter(Boolean).length;
  const totalSections = 7;
  const readinessPercent = Math.round((readySections / totalSections) * 100);

  const handleDownloadPublishPack = async () => {
    if (!selectedApp) return;
    setDownloadingPack(true);
    setError(null);
    try {
      const zip = new JSZip();
      const root = `${selectedApp.name.replace(
        /[^a-zA-Z0-9-_]/g,
        "_",
      )}-publish-pack`;

      const addTextFile = (relativePath: string, content: string) => {
        zip.file(`${root}/${relativePath}`, content);
      };

      const addRemoteFile = async (relativePath: string, url: string) => {
        try {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          const blob = await response.blob();
          zip.file(`${root}/${relativePath}`, blob);
        } catch (fetchError: any) {
          addTextFile(
            `${relativePath}.url.txt`,
            `Could not fetch directly in browser.\nURL: ${url}\nReason: ${
              fetchError?.message || "unknown"
            }`,
          );
        }
      };

      addTextFile(
        "README.txt",
        [
          "Publish Pack",
          `App: ${selectedApp.name}`,
          `Generated: ${new Date().toISOString()}`,
          `Readiness: ${readinessPercent}% (${readySections}/${totalSections})`,
        ].join("\n"),
      );

      if (listings.length > 0) {
        listings.forEach((listing, index) => {
          addTextFile(
            `store-listings/listing-${index + 1}-${listing.platform}.json`,
            JSON.stringify(listing, null, 2),
          );
        });
      }

      if (icons.length > 0) {
        await Promise.all(
          icons.map((icon, index) =>
            addRemoteFile(`icons/icon-${index + 1}.png`, icon.baseImageUrl),
          ),
        );
      }

      if (featureGraphics.length > 0) {
        await Promise.all(
          featureGraphics.map((graphic, index) =>
            addRemoteFile(
              `feature-graphics/feature-graphic-${index + 1}.png`,
              graphic.baseImageUrl,
            ),
          ),
        );
      }

      if (screenshots.length > 0) {
        await Promise.all(
          screenshots.map((shot, index) =>
            addRemoteFile(
              `screenshots/${shot.locale}/${
                index + 1
              }-${`${shot.dimension.name}-${shot.dimension.width}x${shot.dimension.height}`.replace(
                /[^a-zA-Z0-9-_]/g,
                "_",
              )}.png`,
              shot.imageUrl,
            ),
          ),
        );
      }

      if (marketingReports.length > 0) {
        marketingReports.forEach((report, index) => {
          addTextFile(
            `marketing/reports/report-${index + 1}.json`,
            JSON.stringify(report, null, 2),
          );
        });
      }

      if (marketingAssets.length > 0) {
        await Promise.all(
          marketingAssets.map((asset, index) =>
            addRemoteFile(
              `marketing/assets/${asset.platform}/${asset.mediaType}-${
                index + 1
              }.${asset.mediaType === "video" ? "mp4" : "png"}`,
              asset.image?.downloadUrl || asset.video?.downloadUrl || "",
            ),
          ),
        );
      }

      if (violationReports.length > 0) {
        violationReports.forEach((report, index) => {
          addTextFile(
            `violation-reports/report-${index + 1}.json`,
            JSON.stringify(report, null, 2),
          );
        });
        const screenshotLinks = (violationReports[0]?.screenshots || [])
          .map((item) => `${item.fileName}: ${item.downloadUrl || ""}`)
          .join("\n");
        if (screenshotLinks) {
          addTextFile(
            "violation-reports/latest-screenshot-links.txt",
            screenshotLinks,
          );
        }
      }

      if (checklistProgress) {
        addTextFile(
          "deployment-checklist/checklist-progress.json",
          JSON.stringify(checklistProgress, null, 2),
        );
      }

      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${root}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSuccess("Publish pack downloaded.");
    } catch (downloadError: any) {
      console.error(downloadError);
      setError(downloadError?.message || "Failed to create publish pack.");
    } finally {
      setDownloadingPack(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Publish Ready Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Review everything required to publish confidently. Missing sections show
        where to create assets/details.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 2.5, mb: 2.5 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Select a registered app"
              value={selectedAppId}
              onChange={(event) => setSelectedAppId(event.target.value)}
            >
              <MenuItem value="">
                <em>Select a registered app</em>
              </MenuItem>
              {apps.map((app) => (
                <MenuItem key={app.appId} value={app.appId}>
                  {app.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip
                icon={
                  readinessPercent === 100 ? (
                    <CheckCircleIcon />
                  ) : (
                    <WarningIcon />
                  )
                }
                color={readinessPercent === 100 ? "success" : "warning"}
                label={`Readiness: ${readinessPercent}% (${readySections}/${totalSections})`}
              />
              {selectedApp && (
                <Chip label={`App: ${selectedApp.name}`} variant="outlined" />
              )}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadPublishPack}
              disabled={!selectedApp || downloadingPack}
            >
              {downloadingPack
                ? "Building Publish Pack..."
                : "Download Publish Pack"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <SectionCard
            title="App Store Listing"
            ready={listings.length > 0}
            summary={
              listings.length > 0
                ? `${listings.length} listing(s) generated`
                : "No listing found yet."
            }
            ctaLabel="Create Listing"
            onCta={() => navigate("/app-store-listing")}
            actions={
              listings.length > 0
                ? [
                    {
                      label: "Copy Latest JSON",
                      icon: <CopyIcon fontSize="small" />,
                      onClick: async () => {
                        const latest = listings[0];
                        await copyText(
                          ListingService.exportAllEntriesAsJSON(latest),
                        );
                        setSuccess("Copied latest listing JSON.");
                      },
                    },
                    {
                      label: "Download Latest JSON",
                      icon: <DownloadIcon fontSize="small" />,
                      onClick: () => {
                        const latest = listings[0];
                        const safeName =
                          `${latest.appName}-listing-${latest.platform}.json`.replace(
                            /[^a-zA-Z0-9-_\.]/g,
                            "_",
                          );
                        downloadText(
                          ListingService.exportAllEntriesAsJSON(latest),
                          safeName,
                        );
                      },
                    },
                  ]
                : []
            }
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard
            title="App Icon"
            ready={icons.length > 0}
            summary={
              icons.length > 0
                ? `${icons.length} icon concept(s)`
                : "No icon asset found yet."
            }
            ctaLabel="Create Icon"
            onCta={() => navigate("/icon-generator")}
            actions={
              icons.length > 0
                ? [
                    {
                      label: "Download Latest",
                      icon: <DownloadIcon fontSize="small" />,
                      onClick: () =>
                        openDownload(
                          icons[0].baseImageUrl,
                          `${selectedApp?.name || "app"}-icon.png`,
                        ),
                    },
                    {
                      label: "Copy Prompt",
                      icon: <CopyIcon fontSize="small" />,
                      onClick: async () => {
                        await copyText(icons[0].prompt || "");
                        setSuccess("Copied icon prompt.");
                      },
                    },
                  ]
                : []
            }
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard
            title="Feature Graphic"
            ready={featureGraphics.length > 0}
            summary={
              featureGraphics.length > 0
                ? `${featureGraphics.length} feature graphic concept(s)`
                : "No feature graphic found yet."
            }
            ctaLabel="Create Feature Graphic"
            onCta={() => navigate("/feature-graphic-generator")}
            actions={
              featureGraphics.length > 0
                ? [
                    {
                      label: "Download Latest",
                      icon: <DownloadIcon fontSize="small" />,
                      onClick: () =>
                        openDownload(
                          featureGraphics[0].baseImageUrl,
                          `${selectedApp?.name || "app"}-feature-graphic.png`,
                        ),
                    },
                    {
                      label: "Copy Prompt",
                      icon: <CopyIcon fontSize="small" />,
                      onClick: async () => {
                        await copyText(featureGraphics[0].prompt || "");
                        setSuccess("Copied feature graphic prompt.");
                      },
                    },
                  ]
                : []
            }
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard
            title="Store Screenshots"
            ready={screenshots.length > 0}
            summary={
              screenshots.length > 0
                ? `${screenshots.length} screenshot(s)`
                : "No screenshots generated yet."
            }
            ctaLabel="Create Screenshots"
            onCta={() => navigate("/screenshot-creator")}
            actions={
              screenshots.length > 0
                ? [
                    {
                      label: "Download Latest",
                      icon: <DownloadIcon fontSize="small" />,
                      onClick: async () => {
                        const latest = screenshots[0];
                        const fileName = `${selectedApp?.name || "app"}-${
                          latest.locale
                        }-${latest.dimension.name}-${latest.dimension.width}x${
                          latest.dimension.height
                        }.png`.replace(/[^a-zA-Z0-9-_.]/g, "_");
                        await ScreenshotService.downloadScreenshot(
                          latest.imageUrl,
                          fileName,
                        );
                      },
                    },
                    {
                      label: "Open Latest",
                      icon: <OpenInNewIcon fontSize="small" />,
                      onClick: () => openDownload(screenshots[0].imageUrl),
                    },
                  ]
                : []
            }
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard
            title="App Marketing"
            ready={marketingReports.length > 0}
            summary={
              marketingReports.length > 0
                ? `${marketingReports.length} marketing report(s), ${marketingAssets.length} creative asset(s)`
                : "No marketing report yet."
            }
            ctaLabel="Create Marketing Research"
            onCta={() => navigate("/app-marketing")}
            actions={
              marketingReports.length > 0
                ? [
                    {
                      label: "Copy Latest Summary",
                      icon: <CopyIcon fontSize="small" />,
                      onClick: async () => {
                        await copyText(latestMarketingReport?.summary || "");
                        setSuccess("Copied marketing summary.");
                      },
                    },
                    {
                      label: "Download Assets ZIP",
                      icon: <DownloadIcon fontSize="small" />,
                      onClick: async () => {
                        if (!selectedApp) return;
                        const zip =
                          await MarketingService.createMarketingAssetsZip({
                            appId: selectedApp.appId,
                          });
                        openDownload(zip.downloadUrl, zip.fileName);
                        setSuccess("Marketing assets zip generated.");
                      },
                    },
                  ]
                : []
            }
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard
            title="Violation Checker Reports"
            ready={violationReports.length > 0}
            summary={
              violationReports.length > 0
                ? `${violationReports.length} report(s), latest has ${
                    latestViolationReport?.groupedFindings.reduce(
                      (n, g) => n + g.totalIssues,
                      0,
                    ) || 0
                  } issue(s)`
                : "No violation review report yet."
            }
            ctaLabel="Run Violation Checker"
            onCta={() => navigate("/app-store-violation-checker")}
            actions={
              violationReports.length > 0
                ? [
                    {
                      label: "Copy Fix Steps",
                      icon: <CopyIcon fontSize="small" />,
                      onClick: async () => {
                        const text = (
                          latestViolationReport?.groupedFindings || []
                        )
                          .flatMap((group) =>
                            group.issues.flatMap((issue) =>
                              issue.fixSteps.map(
                                (step) =>
                                  `[${group.platform.toUpperCase()}] ${
                                    issue.title
                                  }: ${step}`,
                              ),
                            ),
                          )
                          .join("\n");
                        await copyText(text);
                        setSuccess("Copied violation fix steps.");
                      },
                    },
                    {
                      label: "Download Latest JSON",
                      icon: <DownloadIcon fontSize="small" />,
                      onClick: () => {
                        if (!latestViolationReport) return;
                        const safeName =
                          `${latestViolationReport.appName}-violation-report.json`.replace(
                            /[^a-zA-Z0-9-_\.]/g,
                            "_",
                          );
                        downloadText(
                          JSON.stringify(latestViolationReport, null, 2),
                          safeName,
                        );
                      },
                    },
                  ]
                : []
            }
          />
        </Grid>

        <Grid item xs={12}>
          <SectionCard
            title="Deployment Checklist"
            ready={Boolean(checklistProgress)}
            summary={
              checklistProgress
                ? `Overall ${checklistProgress.overallProgress}% | Android ${checklistProgress.androidProgress}% | iOS ${checklistProgress.iosProgress}%`
                : "No checklist progress found yet."
            }
            ctaLabel="Open Deployment Checklist"
            onCta={() => navigate("/deployment-checklist")}
            actions={
              checklistProgress
                ? [
                    {
                      label: "Copy Progress Summary",
                      icon: <CopyIcon fontSize="small" />,
                      onClick: async () => {
                        const text = `Overall: ${checklistProgress.overallProgress}% | Android: ${checklistProgress.androidProgress}% | iOS: ${checklistProgress.iosProgress}% | Cross-platform: ${checklistProgress.crossPlatformProgress}%`;
                        await copyText(text);
                        setSuccess("Copied checklist summary.");
                      },
                    },
                    {
                      label: "Download Checklist JSON",
                      icon: <DownloadIcon fontSize="small" />,
                      onClick: () => {
                        if (!selectedApp) return;
                        const fileName =
                          `${selectedApp.name}-deployment-checklist.json`.replace(
                            /[^a-zA-Z0-9-_\.]/g,
                            "_",
                          );
                        downloadText(
                          JSON.stringify(checklistProgress, null, 2),
                          fileName,
                        );
                      },
                    },
                  ]
                : []
            }
          />
        </Grid>
      </Grid>

      {loading && (
        <Typography sx={{ mt: 2, color: "text.secondary" }}>
          Loading publish-ready data...
        </Typography>
      )}
    </Box>
  );
};

type SectionAction = {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void | Promise<void>;
};

function SectionCard({
  title,
  ready,
  summary,
  ctaLabel,
  onCta,
  actions,
}: {
  title: string;
  ready: boolean;
  summary: string;
  ctaLabel: string;
  onCta: () => void;
  actions: SectionAction[];
}) {
  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography variant="h6">{title}</Typography>
          <Chip
            icon={ready ? <CheckCircleIcon /> : <WarningIcon />}
            color={ready ? "success" : "warning"}
            label={ready ? "Ready" : "Missing"}
            size="small"
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          {summary}
        </Typography>
      </CardContent>
      <CardActions sx={{ px: 2, pb: 2, pt: 0, flexWrap: "wrap", gap: 1 }}>
        <Button
          size="small"
          variant={ready ? "outlined" : "contained"}
          onClick={onCta}
        >
          {ready ? "Open Section" : ctaLabel}
        </Button>
        {actions.map((action) => (
          <Button
            key={action.label}
            size="small"
            variant="text"
            startIcon={action.icon}
            onClick={() => void action.onClick()}
          >
            {action.label}
          </Button>
        ))}
      </CardActions>
    </Card>
  );
}

export default PublishReadyDashboard;
